import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Button, Alert, Form, Image } from "react-bootstrap";
import { Link } from "react-router-dom";

import { db } from "../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { randomString } from "utils/random";
import { imageToThumb } from "utils/image";
import AdminRecord from "./AdminRecord";

const Visitor = (props) => {
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [name, setName] = useState(undefined);
  const [surname, setSurname] = useState(undefined);
  const [adminRecords, setAdminRecords] = useState([]);
  const [checkinDate, setCheckinDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const inputImage = useRef();
  const refName = useRef();
  const refSurname = useRef();
  const refImage = useRef();
  const refPayment = useRef();
  const refCheckinDate = useRef();
  const [image, setImage] = useState();
  const [payment, setPayment] = useState();

  useEffect(() => {
    setComplete(
      refName?.current?.value && refSurname?.current?.value && image && payment
    );
    return () => {};
  }, [
    name,
    surname,
    refName?.current?.value,
    refSurname?.current?.value,
    image,
    payment,
  ]);

  useEffect(() => {
    const q = query(
      collection(db, "admins"),
      where("dutyDate", "==", checkinDate)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (querySnapshot.empty) {
        setError("No Admins on Duty");
        setTimeout(() => {
          setError("");
        }, 2000);
        setAdminRecords([]);
        return;
      }

      let docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ ...doc.data(), _id: doc.id });
      });

      setAdminRecords(docs);
    });
    return () => unsubscribe;
  }, [checkinDate]);

  let onImageSelect = (event) => {
    console.log(event);
    if (event.target.files[0]) {
      imageToThumb(event.target.files[0], 100).then((i) => {
        setImage(i);
      });
    }
  };

  const submitCheckin = useMemo(
    () => async () => {
      if (
        refName?.current?.value &&
        refSurname?.current?.value &&
        image &&
        payment
      ) {
        let clientId = localStorage.getItem("clientId");
        let lowerName = `${refName.current.value}_${refSurname.current.value}`
          .toLowerCase()
          .replace(/[\W_]+/g, "_");
        if (!clientId) {
          clientId = randomString(16);
          localStorage.setItem("clientId", clientId);
        }
        let d = checkinDate.replace(/[\W_]+/g, "");
        let id = `${d}-${clientId}-${lowerName}`;
        setError("");
        setMessage("");
        let submission = {
          name: refName.current.value,
          surname: refSurname.current.value,
          date: checkinDate,
          paymentOption: payment,
          image: image,
        };
        console.log(submission);
        try {
          await setDoc(doc(db, "checkin", id), submission);
          setMessage("You have successfully checked in");
        } catch {
          setError("Check-in failed");
        }
      }
    },
    [checkinDate, image, payment]
  );

  let content = (
    <>
      <Card className="mt-2">
        <Card.Body>
          <h2 className="text-center mb-4">Pilot Check In</h2>
          {error && <Alert variant="danger">{error}</Alert>}
          {message && <Alert variant="info">{message}</Alert>}
          <Form.Control
            className="mt-3"
            type="date"
            name="date_of_birth"
            error={"Date picker error"}
            value={checkinDate}
            ref={refCheckinDate}
            onChange={(e) => setCheckinDate(e.target.value)}
          />

          <Form onSubmit={submitCheckin}>
            <Form.Group id="name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="name"
                required
                ref={refName}
                onChange={(e) => setName(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group id="surname">
              <Form.Label>Surname</Form.Label>
              <Form.Control
                type="name"
                required
                ref={refSurname}
                onChange={(e) => setSurname(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Label>Payment method</Form.Label>
            <Form.Group
              id="payment"
              onChange={(e) => setPayment(e.target.value)}
            >
              <Form.Check
                inline
                label="Cash"
                value="cash"
                name="paymentOption"
                type={"radio"}
                id={`inline-radio-1`}
              />
              <Form.Check
                inline
                label="Snapscan"
                value="snapscan"
                name="paymentOption"
                type={"radio"}
                id={`inline-radio-2`}
              />
              <Form.Check
                inline
                label="EFT"
                value="eft"
                name="paymentOption"
                type={"radio"}
                id={`inline-radio-3`}
              />
              <div className="w-100 text-center mt-2">
                <a
                  className="w-100 text-center mt-2"
                  href="https://bambiparagliding.co.za/home/site-check-in/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Snapscan and EFT details
                </a>
              </div>
              <div>or</div>
              <Form.Check
                inline
                label="Annual sponsor"
                value="sponsor"
                name="paymentOption"
                type={"radio"}
                id={`inline-radio-4`}
              />
            </Form.Group>

            <input
              style={{
                display: "none",
              }}
              ref={inputImage}
              accept="image/*"
              id="icon-button-file"
              type="file"
              capture="environment"
              onChange={onImageSelect}
            />
            <Image thumbnail src={image}></Image>
            <Button
              className="w-100 mt-4"
              onClick={() => inputImage.current.click()}
            >
              Add a Selfie
            </Button>

            <Button
              disabled={!complete || submitting}
              className="w-100 mt-4"
              onClick={submitCheckin}
            >
              Check In
            </Button>
            {error && <Alert variant="danger">{error}</Alert>}
            {message && <Alert variant="info">{message}</Alert>}
          </Form>
        </Card.Body>
      </Card>
      <Card className="mt-3">
        <Card.Body>
          <h2 className="text-center mb-4">Admins on Duty</h2>
          {adminRecords.map((admin) => (
            <AdminRecord key={admin._id} admin={admin} />
          ))}
        </Card.Body>
      </Card>
      <div className="w-100 text-center mt-2">
        <a
          className="w-100 text-center mt-2"
          href="https://bambiparagliding.co.za"
          target="_blank"
          rel="noreferrer"
        >
          Bambi Paragliding
        </a>
      </div>
      <div className="w-100 text-center mt-2">
        Admin? <Link to="/admin">Sign In</Link>
      </div>
      <div className="w-100 text-center mt-4" style={{ fontSize: "10px" }}>
        Bambi Paragliding Access App version 0.1.11
      </div>
    </>
  );
  return content;
};

export default Visitor;
